import React, { Component } from "react";
import {
	Platform,
	Text,
	View,
	Picker,
	StyleProp,
	ViewStyle,
	CheckBox,
	CheckBoxProps,
	TouchableOpacity,
	Switch,
	TextInput,
	KeyboardTypeOptions,
	NativeSyntheticEvent,
	TextInputFocusEventData,
} from "react-native";
import styles from "../styles";
import theme from "../theme";
import { dynamicSize, getDeepCopy } from "../helpers";
import { ISelectFormField, OnInputChange, IFormInputCustomProps, IFormField, FormListItem } from "../base/models/forms";
import { getKeyboardType } from "../base/render-utils/form";
import { FormField } from "../base/form-validation";
import { DynamicFormValidation, DynamicFormField } from "../base/form-validation/dynamic";

const isIOS: boolean = Platform.OS === "ios";
const CHECKBOX_SIZE: number = isIOS ? 25 : 20;

type CheckboxListState = {
	items: FormListItem[];
};

export default class CheckboxList extends Component<
	{
		field: ISelectFormField;
		fieldData: FormField;
		label: string;
		onInputChange?: OnInputChange;
		color?: any;
		values?: FormListItem[];
		invalid?: boolean;
	} & { customProps?: IFormInputCustomProps },
	CheckboxListState
> {
	private inputElRefs = new Array(this.props.field.selectValues?.length || 0);

	state = {
		items: [],
	};

	componentDidMount() {
		const items: FormListItem[] = [];
		(this.props.field.selectValues || []).forEach((value: string | number) => {
			const item: FormListItem = {
				value,
				checked: false,
			};
			if (this.props.fieldData.form) {
				item.form = new DynamicFormValidation(null);
			}
			items.push(item);
		});

		this.setState({ items });
	}

	onValuesChanged(i: number, checked?: boolean) {
		const items: FormListItem[] = this.state.items;
		const newValue: boolean = checked !== undefined ? checked : !items[i].checked;
		items[i].checked = newValue;

		if (this.props.fieldData.form) {
			const fields: { [key: string]: DynamicFormField & any } = newValue
				? items[i].form?.fields || getDeepCopy(this.props.fieldData.form.fields)
				: null;
			const form: DynamicFormValidation = new DynamicFormValidation(fields);
			items[i].form = form;
		}

		this.setState({ items });
		this.props.onInputChange(this.props.field, items);
	}

	renderIOS(item: any, i: number) {
		return (
			<Switch
				value={this.state.items[i]?.checked || false}
				onValueChange={(checked) => {
					this.onValuesChanged(i, checked);
				}}
				trackColor={{ false: undefined, true: theme.colors.orange }}
			/>
		);
	}

	renderNestedField(field: FormField, i: number) {
		const fieldConfig: IFormField = (field as any) as IFormField;
		const inputKeyboardType: KeyboardTypeOptions = getKeyboardType(fieldConfig.fieldType);
		const invalidStyles = !field.valid && field.touched ? styles.form.invalidInput : {};
		const mobileStyles = {
			color: theme.colors.orange,
			fontSize: 19,
			paddingLeft: 8,
			paddingRight: 10,
			paddingTop: 5,
			paddingBottom: 5,
			marginTop: 3,
			width: "93%",
			flex: 1,
			flexDirection: "row",
		};
		const inputStyle = [
			{
				borderWidth: 1,
				marginLeft: dynamicSize(10),
				paddingLeft: 4,
				paddingRight: 4,
				paddingTop: 2,
				paddingBottom: 2,
				fontSize: 13,
				borderColor: theme.colors.grey8,
				...Platform.select({
					ios: mobileStyles,
					android: mobileStyles,
				}),
			},
			invalidStyles,
		];

		return (
			<TextInput
				key={field.key}
				style={inputStyle}
				placeholder={fieldConfig.placeholder}
				placeholderTextColor={theme.colors.grey4}
				defaultValue={this.state.items[i].form.fields[field.key].value}
				keyboardType={inputKeyboardType}
				// selectionColor={theme.colors.orange}
				underlineColorAndroid="transparent"
				onChangeText={(text: string) => {
					console.log("onChangeText:", text);
					const items = this.state.items;
					items[i].form = items[i].form.formFieldOnBlur(field.key, text);
					this.setState({ items });
				}}
				// textContentType={
				// 	customProps.field.fieldType === "email" ? "emailAddress" : "none"
				// }
				onBlur={(e: NativeSyntheticEvent<TextInputFocusEventData>) => {
					setTimeout(() => {
						this.props.onInputChange(this.props.field, this.state.items);
						let total: number = 0;
						this.state.items.forEach((item: FormListItem) => {
							if (item.form && item.form.fields) total += Number(item.form.fields[field.key]?.value) || 0;
						});
						this.props.onInputChange({ ...field, id: field.key } as any, total);
					}, 1);
				}}
			/>
		);
	}

	render() {
		const props = { color: theme.colors.orange };
		const invalidStyles = this.props.invalid ? styles.form.invalidInput : {};
		const customProps: IFormInputCustomProps = this.props.customProps || ({} as IFormInputCustomProps);

		return (
			<View
				style={[styles.form.group, invalidStyles]}
				nativeID={"laco-form-checkbox-container-" + this.props.field.id}
			>
				{this.props.label && (
					<Text
						nativeID={"laco-form-input-label-" + this.props.field.id}
						style={[styles.text.formLabel, { marginBottom: dynamicSize(20) }]}
					>
						{this.props.label.toUpperCase()}
						{customProps.showAsterisks && customProps.asteriskTemplate}
					</Text>
				)}
				{this.state.items.map((item: FormListItem, i: number) => {
					return (
						<View style={{ flexDirection: "row", marginBottom: dynamicSize(15) }} key={i}>
							{isIOS && this.renderIOS(item, i)}
							{!isIOS && (
								<CheckBox
									nativeID={`laco-form-checkbox-checkbox-${i}`}
									ref={(component) => (this.inputElRefs[i] = component)}
									color={theme.colors.orange}
									style={{
										height: dynamicSize(CHECKBOX_SIZE),
										width: dynamicSize(CHECKBOX_SIZE),
										marginRight: 5,
										// backgroundColor: "transparent",
										...Platform.select({
											web: {
												backgroundColor: "transparent",
											},
										}),
									}}
									value={this.state.items[i]?.checked || false}
									onValueChange={(checked) => {
										this.onValuesChanged(i, checked);
									}}
									{...props}
								/>
							)}
							<View nativeID={`laco-form-checkbox-container-label-${i}`}>
								<TouchableOpacity
									onPress={() => {
										this.onValuesChanged(i);
									}}
								>
									<Text
										nativeID={`laco-form-checkbox-label-${i}`}
										style={[
											styles.text.formInput,
											{
												marginLeft: dynamicSize(10),
												marginRight: dynamicSize(10),
												height: dynamicSize(CHECKBOX_SIZE),
												lineHeight: dynamicSize(CHECKBOX_SIZE),
												color: theme.colors.grey8,
												width: "100%",
												flex: 1,
												flexDirection: "row",
												flexGrow: 1,
											},
										]}
									>
										{this.state.items[i].value}
									</Text>
								</TouchableOpacity>
								{this.state.items[i].checked && !!this.state.items[i].form && (
									<>
										{this.state.items[i].form.getOrderedFields().map((nestedField: FormField) => {
											return this.renderNestedField(nestedField, i);
										})}
									</>
								)}
							</View>
						</View>
					);
				})}
			</View>
		);
	}
}
